import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/common/Logo';
import { useValidation } from '../hooks/useValidation';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { deriveKeys } from '../crypto/kdf';

/**
 * Check if a GraphQL error requires Turnstile verification
 */
function isTurnstileRequiredError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message;
    // Check for TURNSTILE_REQUIRED error code in the message
    return message.includes('TURNSTILE_REQUIRED') ||
           message.includes('Verification required');
  }
  return false;
}

/**
 * Check if a GraphQL error indicates invalid Turnstile token
 */
function isTurnstileInvalidError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message;
    return message.includes('TURNSTILE_INVALID') ||
           message.includes('Verification failed');
  }
  return false;
}

// Device verification step
type LoginStep = 'credentials' | 'device-verification';

export default function Login() {
  const navigate = useNavigate();
  const { login, sendDeviceVerificationCode, verifyDeviceCode, deviceRevokedMessage, clearDeviceRevokedMessage } = useAuth();
  const { validateEmail } = useValidation();

  // Login step state
  const [step, setStep] = useState<LoginStep>('credentials');

  // Show device revoked message if present
  const [showRevokedAlert, setShowRevokedAlert] = useState(false);
  const [revokedMessage, setRevokedMessage] = useState<string | null>(null);

  useEffect(() => {
    if (deviceRevokedMessage) {
      setRevokedMessage(deviceRevokedMessage);
      setShowRevokedAlert(true);
      clearDeviceRevokedMessage();
    }
  }, [deviceRevokedMessage, clearDeviceRevokedMessage]);

  // Credentials step state
  const [email, setEmail] = useState('');
  const [masterPassword, setMasterPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileRequired, setTurnstileRequired] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // Device verification step state
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [vaultKeyForVerification, setVaultKeyForVerification] = useState<Uint8Array | null>(null);
  const cooldownInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up cooldown interval
  useEffect(() => {
    return () => {
      if (cooldownInterval.current) {
        clearInterval(cooldownInterval.current);
      }
    };
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (cooldownSeconds > 0) {
      cooldownInterval.current = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            if (cooldownInterval.current) {
              clearInterval(cooldownInterval.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (cooldownInterval.current) {
        clearInterval(cooldownInterval.current);
      }
    };
  }, [cooldownSeconds]);

  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleTurnstileError = useCallback((errorMsg: string) => {
    setError(errorMsg);
    setTurnstileToken(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!masterPassword) {
      setError('Please enter your master password');
      return;
    }

    // If Turnstile is required but no token, show error
    if (turnstileRequired && !turnstileToken) {
      setError('Please complete the verification');
      return;
    }

    setLoading(true);

    try {
      const result = await login(email, masterPassword, turnstileToken || undefined);

      if (result.requiresDeviceVerification && result.challengeId) {
        // Device verification required
        setChallengeId(result.challengeId);

        // Derive vault key for later use after verification
        if (result.kdfParams) {
          const keys = await deriveKeys(masterPassword, result.kdfParams);
          setVaultKeyForVerification(keys.vaultKey);
        }

        setStep('device-verification');
        setTurnstileRequired(false);
        setTurnstileToken(null);
      } else if (result.success) {
        // Clear Turnstile state on successful login
        setTurnstileRequired(false);
        setTurnstileToken(null);
        navigate('/vault');
      }
    } catch (err) {
      // Check if Turnstile is now required
      if (isTurnstileRequiredError(err)) {
        setTurnstileRequired(true);
        setError('Too many failed attempts. Please complete verification.');
      } else if (isTurnstileInvalidError(err)) {
        // Token was invalid, need to get a new one
        setTurnstileToken(null);
        setError('Verification failed. Please try again.');
      } else {
        // Reset token on any error (tokens are single-use)
        setTurnstileToken(null);
        setError(err instanceof Error ? err.message : 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!challengeId) return;

    setSendingCode(true);
    setError('');

    try {
      const result = await sendDeviceVerificationCode(challengeId);

      if (result.success) {
        setCodeSent(true);
        setCooldownSeconds(60); // 60 second cooldown
      } else if (result.retryAfter) {
        setCooldownSeconds(result.retryAfter);
        setError(result.message || 'Please wait before requesting another code');
      } else {
        setError(result.message || 'Failed to send code');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!challengeId || !vaultKeyForVerification) {
      setError('Verification session expired. Please try logging in again.');
      setStep('credentials');
      return;
    }

    if (verificationCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const result = await verifyDeviceCode(
        challengeId,
        verificationCode,
        trustDevice,
        vaultKeyForVerification
      );

      if (result.success) {
        navigate('/vault');
      } else {
        if (result.remainingAttempts !== undefined && result.remainingAttempts <= 0) {
          setError('Too many failed attempts. Please request a new code.');
          setCodeSent(false);
          setVerificationCode('');
        } else {
          setError(result.message || 'Invalid code');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setChallengeId(null);
    setVerificationCode('');
    setCodeSent(false);
    setVaultKeyForVerification(null);
    setError('');
  };

  // Render credentials form
  const renderCredentialsForm = () => (
    <form onSubmit={handleSubmit}>
      <TextField
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        margin="normal"
        autoComplete="email"
        autoFocus
      />

      <TextField
        fullWidth
        label="Master Password"
        type={showPassword ? 'text' : 'password'}
        value={masterPassword}
        onChange={(e) => setMasterPassword(e.target.value)}
        margin="normal"
        autoComplete="current-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {turnstileRequired && (
        <TurnstileWidget
          onSuccess={handleTurnstileSuccess}
          onExpire={handleTurnstileExpire}
          onError={handleTurnstileError}
          helperText={!turnstileToken ? 'Complete verification to continue' : undefined}
        />
      )}

      <Button
        fullWidth
        type="submit"
        variant="contained"
        size="large"
        disabled={loading || (turnstileRequired && !turnstileToken)}
        sx={{ mt: turnstileRequired ? 2 : 3, mb: 2 }}
      >
        {loading ? 'Unlocking...' : 'Unlock Vault'}
      </Button>

      <Typography align="center" color="text.secondary">
        Don't have an account?{' '}
        <Link component={RouterLink} to="/signup">
          Create one
        </Link>
      </Typography>
    </form>
  );

  // Render device verification form
  const renderDeviceVerificationForm = () => (
    <form onSubmit={handleVerifyCode}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <EmailIcon sx={{ fontSize: 32, color: 'white' }} />
        </Box>
        <Typography variant="h6" gutterBottom>
          Verify New Device
        </Typography>
        <Typography color="text.secondary" variant="body2">
          We detected a sign-in from a new device. For your security, please verify it's you.
        </Typography>
      </Box>

      {!codeSent ? (
        <Button
          fullWidth
          variant="contained"
          onClick={handleSendCode}
          disabled={sendingCode || cooldownSeconds > 0}
          sx={{ mb: 2 }}
        >
          {sendingCode ? (
            <CircularProgress size={24} color="inherit" />
          ) : cooldownSeconds > 0 ? (
            `Resend in ${cooldownSeconds}s`
          ) : (
            'Send Verification Code'
          )}
        </Button>
      ) : (
        <>
          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setVerificationCode(value);
            }}
            margin="normal"
            placeholder="000000"
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.5rem' },
            }}
            helperText="Enter the 6-digit code sent to your email"
            autoFocus
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                color="primary"
              />
            }
            label="Trust this device for future logins"
            sx={{ mt: 1, mb: 2 }}
          />

          <Button
            fullWidth
            type="submit"
            variant="contained"
            size="large"
            disabled={loading || verificationCode.length !== 6}
            sx={{ mb: 2 }}
          >
            {loading ? 'Verifying...' : 'Verify & Sign In'}
          </Button>

          <Button
            fullWidth
            variant="text"
            onClick={handleSendCode}
            disabled={sendingCode || cooldownSeconds > 0}
            sx={{ mb: 2 }}
          >
            {cooldownSeconds > 0 ? `Resend code in ${cooldownSeconds}s` : 'Resend code'}
          </Button>
        </>
      )}

      <Button
        fullWidth
        variant="text"
        color="inherit"
        onClick={handleBackToCredentials}
      >
        ← Back to sign in
      </Button>
    </form>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="xs">
        <Paper elevation={0} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Logo size="large" />
            </Box>
            {step === 'credentials' && (
              <Typography color="text.secondary">
                Sign in to your vault
              </Typography>
            )}
          </Box>

          {showRevokedAlert && revokedMessage && (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              onClose={() => setShowRevokedAlert(false)}
            >
              {revokedMessage}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {step === 'credentials' ? renderCredentialsForm() : renderDeviceVerificationForm()}
        </Paper>
      </Container>
    </Box>
  );
}

